import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getProviderReviews, submitProviderReview } from '../lib/supabase'
import { Star, X, MessageSquare, User, ChevronDown, ChevronUp, Check } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

// Star rating input
const StarRating = ({ rating, onRate, size = 20 }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => onRate(star)}
        className="transition-colors"
      >
        <Star
          size={size}
          className={star <= rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}
        />
      </button>
    ))}
  </div>
)

// Single review display
const ReviewItem = ({ review }) => {
  const reviewerName = review.profiles?.full_name || 'RDC Member'
  const initial = reviewerName.charAt(0).toUpperCase()

  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-semibold text-rdc-primary">{initial}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-black">{reviewerName.split(' ')[0]}</span>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  size={12}
                  className={star <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-200'}
                />
              ))}
            </div>
            <span className="text-xs text-rdc-taupe">
              {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
            </span>
          </div>
          {review.service_type && (
            <span className="inline-block text-xs px-2 py-0.5 bg-rdc-cream text-rdc-taupe rounded-full mb-1.5">
              {review.service_type}
            </span>
          )}
          {review.vehicle_info && (
            <span className="inline-block text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full mb-1.5 ml-1">
              {review.vehicle_info}
            </span>
          )}
          {review.review_text && (
            <p className="text-sm text-rdc-charcoal leading-relaxed">{review.review_text}</p>
          )}
        </div>
      </div>
    </div>
  )
}

// Review form modal
const ReviewForm = ({ provider, onClose, onSubmitted }) => {
  const { user } = useAuth()
  const [rating, setRating] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [serviceType, setServiceType] = useState('')
  const [vehicleInfo, setVehicleInfo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (rating === 0) {
      setError('Please select a rating')
      return
    }
    setSubmitting(true)
    setError('')

    try {
      const { error: submitError } = await submitProviderReview({
        provider_id: provider.id,
        reviewer_id: user.id,
        rating,
        review_text: reviewText.trim() || null,
        service_type: serviceType.trim() || null,
        vehicle_info: vehicleInfo.trim() || null
      })

      if (submitError) throw submitError

      setSubmitted(true)
      setTimeout(() => {
        onSubmitted()
        onClose()
      }, 1500)
    } catch (err) {
      console.error('Error submitting review:', err)
      setError('Failed to submit review. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-display font-semibold text-black">
            Review {provider.name}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X size={18} />
          </button>
        </div>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <Check size={24} className="text-green-600" />
            </div>
            <p className="font-medium text-black">Thank you for your review!</p>
            <p className="text-sm text-rdc-taupe mt-1">Your feedback helps fellow members.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-black mb-2">Your Rating *</label>
              <StarRating rating={rating} onRate={setRating} size={28} />
            </div>

            {/* Service Type */}
            <div>
              <label className="block text-sm font-medium text-black mb-1.5">What service did they perform?</label>
              <input
                type="text"
                value={serviceType}
                onChange={e => setServiceType(e.target.value)}
                placeholder="e.g. Engine rebuild, PPF install, Transport"
                className="input-field text-sm"
              />
            </div>

            {/* Vehicle */}
            <div>
              <label className="block text-sm font-medium text-black mb-1.5">Which vehicle?</label>
              <input
                type="text"
                value={vehicleInfo}
                onChange={e => setVehicleInfo(e.target.value)}
                placeholder="e.g. 2019 Porsche 911 GT3"
                className="input-field text-sm"
              />
            </div>

            {/* Review Text */}
            <div>
              <label className="block text-sm font-medium text-black mb-1.5">Your experience</label>
              <textarea
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                placeholder="Share your experience with other members..."
                className="input-field text-sm min-h-[80px]"
                rows={3}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || rating === 0}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// Main reviews section for a provider card
export default function ProviderReviews({ provider, compact = false }) {
  const { user } = useAuth()
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const loadReviews = async () => {
    if (!provider.id || typeof provider.id !== 'string') return
    setLoading(true)
    const { data } = await getProviderReviews(provider.id)
    setReviews(data || [])
    setLoading(false)
  }

  useEffect(() => {
    if (expanded && reviews.length === 0) {
      loadReviews()
    }
  }, [expanded])

  // Compact mode - just show review button and count
  if (compact) {
    return (
      <>
        {user && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 text-xs text-rdc-primary hover:text-rdc-primary/80 font-medium transition-colors"
          >
            <MessageSquare size={12} />
            Write a Review
          </button>
        )}
        {showForm && (
          <ReviewForm
            provider={provider}
            onClose={() => setShowForm(false)}
            onSubmitted={loadReviews}
          />
        )}
      </>
    )
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs font-medium text-rdc-taupe hover:text-black transition-colors"
        >
          <MessageSquare size={13} />
          <span>Member Reviews</span>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {user && (
          <button
            onClick={() => setShowForm(true)}
            className="text-xs text-rdc-primary hover:text-rdc-primary/80 font-medium transition-colors"
          >
            Write a Review
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-3">
          {loading ? (
            <p className="text-xs text-rdc-taupe py-2">Loading reviews...</p>
          ) : reviews.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {reviews.slice(0, 5).map(review => (
                <ReviewItem key={review.id} review={review} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-rdc-taupe py-2">
              No member reviews yet. Be the first to share your experience!
            </p>
          )}
        </div>
      )}

      {showForm && (
        <ReviewForm
          provider={provider}
          onClose={() => setShowForm(false)}
          onSubmitted={() => {
            loadReviews()
            setExpanded(true)
          }}
        />
      )}
    </div>
  )
}
